import axios from "axios";
import { backendUrl } from "@/config";
import { translate } from "@/i18n/translate";

/**
 * The station tablet's own client: its sign-in (a station account, mobile-scoped token bound to the
 * branch) lives apart from the admin's, and a 401 signs the tablet out instead of reloading the page.
 */

const STORAGE_KEY = "pizza-station:identity:v1";

export interface StationIdentity {
  login: string;
  token: string;
}

export interface StationProduct {
  id: string;
  name: string;
  recipe_id: string;
  /** the ingredients that tell the pizza apart (not the dough, mozzarella, tomato sauce every pizza has) */
  ingredients: string[];
  /** the branch's pizzas of it in the last 30 days */
  uses: number;
}

export interface StationProductsResponse {
  branch: { id: string; name: string } | null;
  products: StationProduct[];
}

export interface StationCaptureResponse {
  pizza_id: string;
  product_id: string;
  name: string;
  capture_at: string;
  capture_count: number;
}

export interface StationCapture {
  pizza_id: string;
  product_id: string | null;
  name: string | null;
  capture_at: string | null;
}

export function readIdentity(): StationIdentity | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null") as StationIdentity | null;
    return parsed?.token ? parsed : null;
  } catch {
    return null;
  }
}

export function storeIdentity(identity: StationIdentity | null) {
  try {
    if (identity) localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // private mode: signed in as long as the page lives
  }
}

let onSignedOut: (() => void) | null = null;
/** what happens when the backend refuses the token (expired, account disabled) */
export const setSignedOutHandler = (handler: (() => void) | null) => {
  onSignedOut = handler;
};

const client = axios.create({ baseURL: backendUrl, timeout: 30_000 });

client.interceptors.request.use((config) => {
  const identity = readIdentity();
  if (identity) config.headers["Authorization"] = `Bearer ${identity.token}`;
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && readIdentity()) {
      storeIdentity(null);
      onSignedOut?.();
    }
    return Promise.reject(error);
  },
);

export async function signIn(login: string, password: string): Promise<StationIdentity> {
  const { data } = await axios.post<{ token: string }>(`${backendUrl.replace(/\/+$/, "")}/api/admin/auth/token`, {
    login,
    password,
  });
  const identity = { login: login.trim().toLowerCase(), token: data.token };
  storeIdentity(identity);
  return identity;
}

export async function fetchProducts(): Promise<StationProductsResponse> {
  const { data } = await client.get<StationProductsResponse>("/api/mobile/station/products");
  return data;
}

export async function capture(productId: string): Promise<StationCaptureResponse> {
  const { data } = await client.post<StationCaptureResponse>("/api/mobile/station/captures", { product_id: productId });
  return data;
}

export async function fetchCaptures(limit = 12): Promise<StationCapture[]> {
  const { data } = await client.get<{ captures: StationCapture[] }>("/api/mobile/station/captures", { params: { limit } });
  return data.captures;
}

/** sizes of the photo in percent of the camera frame (4056 px wide) */
export const PHOTO_SCALE = { screen: 35, thumbnail: 10 } as const;

/** The camera's photo of the pizza; rejects with 404 until the camera's upload arrives. */
export async function fetchPhoto(pizzaId: string, scale: number, signal?: AbortSignal): Promise<Blob> {
  const { data } = await client.get<Blob>(`/api/mobile/station/pizzas/${encodeURIComponent(pizzaId)}/photo`, {
    params: { scale },
    responseType: "blob",
    signal,
  });
  return data;
}

/** the backend's reason, else the network error */
export function errorMessage(error: unknown): string {
  const e = error as { response?: { data?: { error?: string } }; message?: string };
  return e?.response?.data?.error ?? e?.message ?? translate("station.error.unknown");
}

export const httpStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;
